import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import paymentService from '../services/paymentService';
import { useAuth } from '../contexts/AuthContext';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatCurrency = (value = 0) => `Rs ${Number(value || 0).toFixed(2)}`;

const PaymentsPage = () => {
  const { user } = useAuth();
  const isAdmin = ['ADMIN', 'CHAIRMAN'].includes(user?.role);

  const [month, setMonth] = useState(getCurrentMonth());
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [preview, setPreview] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [uanNo, setUanNo] = useState('');
  const [transactionFile, setTransactionFile] = useState(null);
  const [creatingPayment, setCreatingPayment] = useState(false);

  const currentUserId = user?._id || user?.id || user?.userId;

  const bootstrap = useCallback(async () => {
    try {
      if (isAdmin) {
        const response = await paymentService.getPayrollUsers();
        const list = response.data || [];
        setUsers(list);
        if (list.length > 0) {
          setSelectedUserId((prev) => prev || list[0]._id);
        }
      } else if (currentUserId) {
        setSelectedUserId(currentUserId);
        setUsers([{ _id: currentUserId, name: user.name || 'My Salary', role: user.role }]);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load payroll users');
    }
  }, [currentUserId, isAdmin, user?.name, user?.role]);

  const fetchPreview = useCallback(async () => {
    if (!selectedUserId || !month) return;

    try {
      const response = await paymentService.getSalaryPreview(selectedUserId, month);
      setPreview(response.data);
    } catch (error) {
      setPreview(null);
      toast.error(error.response?.data?.error || 'Failed to load salary preview');
    }
  }, [month, selectedUserId]);

  const fetchHistory = useCallback(async (page = 1) => {
    if (!selectedUserId || !month) return;

    try {
      setLoading(true);
      const response = await paymentService.getHistory({
        userId: selectedUserId,
        month,
        page,
        limit: pagination.limit
      });

      setHistoryRows(response.data?.data || []);
      setPagination(response.data?.pagination || { page: 1, pages: 1, total: 0, limit: 10 });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [month, pagination.limit, selectedUserId]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (!selectedUserId) return;
    fetchPreview();
    fetchHistory(1);
  }, [fetchHistory, fetchPreview, month, selectedUserId]);

  const handleCreatePayment = async () => {
    if (!selectedUserId || !month) {
      toast.error('Please select employee and month');
      return;
    }

    setShowCreateModal(true);
  };

  const submitCreatePayment = async () => {
    if (!transactionId || transactionId.trim().length < 3) {
      toast.error('Please enter a valid Transaction ID (minimum 3 characters)');
      return;
    }

    if (!transactionFile) {
      toast.error('Please upload a transaction document');
      return;
    }

    try {
      setCreatingPayment(true);
      const response = await paymentService.createPayment({
        userId: selectedUserId,
        month,
        transactionId: transactionId.trim(),
        uanNo: uanNo.trim(),
        file: transactionFile
      });
      const warning = response?.data?.generationWarning;
      if (warning) {
        toast.warning(warning);
      } else {
        toast.success('Payment and payslip generated successfully');
      }
      setShowCreateModal(false);
      setTransactionId('');
      setUanNo('');
      setTransactionFile(null);
      await fetchHistory(1);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create payment record');
    } finally {
      setCreatingPayment(false);
    }
  };

  const handleRegenerateSlip = async (paymentId) => {
    try {
      setRegeneratingId(paymentId);
      await paymentService.regenerateSlip(paymentId);
      toast.success('Payslip regenerated successfully');
      await fetchHistory(pagination.page || 1);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to regenerate payslip');
    } finally {
      setRegeneratingId('');
    }
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-charcoal">Payments</h1>
            <p className="text-sm text-gray-600">Monthly payroll for HR and HRO users</p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            />

            {isAdmin ? (
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 min-w-[220px]"
              >
                {users.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name} ({emp.role})
                  </option>
                ))}
              </select>
            ) : (
              <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm">
                {user?.name} ({user?.role})
              </div>
            )}

            {isAdmin && (
              <button
                onClick={handleCreatePayment}
                className="px-4 py-2 bg-olive text-white rounded-lg"
              >
                Create Payment
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-xl font-semibold text-charcoal mb-3">Salary Preview</h2>

          {!preview ? (
            <p className="text-gray-500">No preview available for selected month/user.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-1">
                <p><span className="font-medium">Employee:</span> {preview.user?.name}</p>
                <p><span className="font-medium">Job Role:</span> {preview.user?.currentJobRole || preview.user?.role}</p>
                <p><span className="font-medium">Month:</span> {preview.month}</p>
                <p><span className="font-medium">Base Salary:</span> {formatCurrency(preview.baseSalary)}</p>
                <p><span className="font-medium">Gross Salary:</span> {formatCurrency(preview.grossSalary)}</p>
                <p><span className="font-medium">Absent Days:</span> {preview.absentDays}</p>
                <p><span className="font-medium">Worked Hours:</span> {preview.workedHours}</p>
                <p><span className="font-medium">Adjusted Working Hours:</span> {preview.adjustedWorkingHours}</p>
              </div>

              <div className="space-y-1">
                <p><span className="font-medium">DA:</span> {formatCurrency(preview.allowances?.da)}</p>
                <p><span className="font-medium">HRA:</span> {formatCurrency(preview.allowances?.hra)}</p>
                <p><span className="font-medium">Special Allowance:</span> {formatCurrency(preview.allowances?.specialAllowance)}</p>
                <p><span className="font-medium">Other Allowance:</span> {formatCurrency(preview.allowances?.otherAllowance)}</p>
                <p><span className="font-medium">Total Allowances:</span> {formatCurrency(preview.totalAllowances)}</p>
                <p><span className="font-medium">Payable Gross:</span> {formatCurrency(preview.payableGross)}</p>
                <p><span className="font-medium">LOP:</span> {formatCurrency(preview.lopAmount)}</p>
                <p><span className="font-medium">Total Deductions:</span> {formatCurrency(preview.totalDeductions)}</p>
              </div>

              <div className="md:col-span-2 border-t pt-3 mt-2">
                <p className="text-xl font-bold text-olive">
                  Net Salary: {formatCurrency(preview.netSalary)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-3">
          <h2 className="text-xl font-semibold text-charcoal">Payment History</h2>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Month</Th>
                  <Th>Name</Th>
                  <Th>Absent Days</Th>
                  <Th>Net Salary</Th>
                  <Th>Status</Th>
                  <Th>Transaction ID</Th>
                  <Th>Txn Doc</Th>
                  <Th>Paid At</Th>
                  <Th>Payslip</Th>
                  {isAdmin && <Th>Action</Th>}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr><Td colSpan={isAdmin ? 10 : 9}>Loading...</Td></tr>
                ) : historyRows.length === 0 ? (
                  <tr><Td colSpan={isAdmin ? 10 : 9}>No payment records found for selected month.</Td></tr>
                ) : historyRows.map((row) => (
                  <tr key={row._id} className="border-t">
                    <Td>{row.month}</Td>
                    <Td>{row.userName}</Td>
                    <Td>{row.absentDays}</Td>
                    <Td>{formatCurrency(row.netSalary)}</Td>
                    <Td>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {row.status}
                      </span>
                    </Td>
                    <Td>{row.transactionId || '-'}</Td>
                    <Td>
                      {row.transactionDocumentUrl ? (
                        <a href={row.transactionDocumentUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                          View Doc
                        </a>
                      ) : '-'}
                    </Td>
                    <Td>{row.paidAt ? new Date(row.paidAt).toLocaleString() : '-'}</Td>
                    <Td>
                      {row.payslipUrl ? (
                        <a href={row.payslipUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                          View Slip
                        </a>
                      ) : '-'}
                    </Td>
                    {isAdmin && (
                      <Td>
                        {!row.payslipUrl ? (
                          <button
                            onClick={() => handleRegenerateSlip(row._id)}
                            disabled={regeneratingId === row._id}
                            className="px-3 py-1 bg-charcoal text-white rounded-md"
                          >
                            {regeneratingId === row._id ? 'Generating...' : 'Regenerate Slip'}
                          </button>
                        ) : (
                          <span className="text-gray-500">Completed</span>
                        )}
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Page {pagination.page || 1} of {pagination.pages || 1}
            </p>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={!pagination.page || pagination.page <= 1}
                onClick={() => fetchHistory((pagination.page || 1) - 1)}
              >
                Previous
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={!pagination.page || pagination.page >= pagination.pages}
                onClick={() => fetchHistory((pagination.page || 1) + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-5 space-y-4">
            <h3 className="text-xl font-semibold text-charcoal">Create Payment</h3>
            <p className="text-sm text-gray-600">Month: {month}</p>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Transaction ID</label>
              <input
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Enter transaction ID"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">UAN No (Optional)</label>
              <input
                value={uanNo}
                onChange={(e) => setUanNo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Enter UAN number"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Transaction Document</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setTransactionFile(e.target.files?.[0] || null)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                className="px-4 py-2 border rounded-lg"
                onClick={() => {
                  if (creatingPayment) return;
                  setShowCreateModal(false);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-olive text-white rounded-lg disabled:opacity-50"
                onClick={submitCreatePayment}
                disabled={creatingPayment}
              >
                {creatingPayment ? 'Creating...' : 'Create Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

    </MainLayout>
  );
};

const Th = ({ children }) => (
  <th className="px-3 py-2 text-left text-xs uppercase text-gray-600">{children}</th>
);

const Td = ({ children, colSpan }) => (
  <td className="px-3 py-2" colSpan={colSpan}>{children}</td>
);

export default PaymentsPage;
